import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { JournalEntry, JournalAttachment } from "@/types/journal";
import JournalEntryList from "@/components/journal/JournalEntryList";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const AllJournalEntries = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const location = useLocation();
  const queryClient = useQueryClient();

  // Add a ref to track if we've already highlighted
  const hasHighlighted = React.useRef(false);

  useEffect(() => {
    const updateAllEntries = async () => {
      try {
        const { error } = await supabase
          .from('journal_entries')
          .update({ 
            shareable_with_therapist: true 
          } as Partial<JournalEntry>)
          .is('shareable_with_therapist', null);

        if (error) throw error;
        
        // Invalidate the query to refresh the entries list
        await queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
      } catch (error) {
        console.error('Error updating entries:', error);
      }
    };

    updateAllEntries();
  }, [queryClient]);

  const {
    data: entries,
    isLoading: isLoadingEntries,
  } = useQuery({
    queryKey: ["journal-entries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("journal_entries")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      console.log("Fetched entries:", data?.length || 0);
      return (data || []) as JournalEntry[];
    },
    staleTime: 0, // Always refetch when the query is invalidated
  });

  const {
    data: entryAttachments,
    isLoading: isLoadingAttachments,
  } = useQuery({
    queryKey: ["journal-attachments"],
    queryFn: async () => {
      if (!entries || entries.length === 0) return {};
      
      console.log("Fetching attachments for entries:", entries.map(e => e.id));
      const attachmentsMap: Record<string, JournalAttachment[]> = {};
      
      // Fetch all attachments in parallel
      const attachmentPromises = entries.map(async (entry) => {
        try {
          console.log("Fetching attachments for entry:", entry.id);
          const { data, error } = await supabase.rpc("get_journal_attachments", { entry_id: entry.id });
          if (error) {
            console.error("Error fetching attachments for entry:", entry.id, error);
            return;
          }
          if (data && data.length > 0) {
            console.log("Found attachments for entry:", entry.id, data);
            attachmentsMap[entry.id] = data as JournalAttachment[];
          } else {
            console.log("No attachments found for entry:", entry.id);
          }
        } catch (err) {
          console.error("Error fetching attachments for entry:", entry.id, err);
        }
      });

      await Promise.all(attachmentPromises);
      console.log("Final attachments map:", attachmentsMap);
      return attachmentsMap;
    },
    enabled: !!entries && entries.length > 0,
    staleTime: 0, // Always refetch when the query is invalidated
  });

  // Function to apply highlight
  const applyHighlight = React.useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("!border-4", "!border-blue-500", "!shadow-lg");
      setTimeout(() => {
        el.classList.remove("!border-4", "!border-blue-500", "!shadow-lg");
      }, 2000);
    }
  }, []);

  // Handle hash changes
  React.useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      // Small delay to ensure the DOM is ready
      setTimeout(() => {
        applyHighlight(id);
      }, 100);
    }
  }, [location.hash, applyHighlight]);

  // Handle initial load with hash
  React.useEffect(() => {
    if (!hasHighlighted.current && location.hash && entries) {
      const id = location.hash.replace("#", "");
      // Small delay to ensure the DOM is ready
      setTimeout(() => {
        applyHighlight(id);
        hasHighlighted.current = true;
      }, 100);
    }
  }, [entries, location.hash, applyHighlight]);

  const handleDeleteSelectedEntries = async (entryIds: string[]) => {
    for (const entryId of entryIds) {
      const attachments = entryAttachments?.[entryId] ?? [];
      for (const att of attachments) {
        await supabase.storage.from("journal_attachments").remove([att.file_path]);
        await supabase.from("journal_attachments").delete().eq("id", att.id);
      }
      const { error } = await supabase.from("journal_entries").delete().eq("id", entryId);
      if (error) throw error;
    }
    toast.success(`${entryIds.length} ${entryIds.length === 1 ? "entry" : "entries"} deleted`);
    await queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
    await queryClient.invalidateQueries({ queryKey: ["journal-attachments"] });
  };

  const handleRemoveEntryAttachment = async (entryId: string, attachmentId: string) => {
    try {
      const attachment = entryAttachments?.[entryId]?.find(a => a.id === attachmentId);
      
      if (!attachment) {
        console.error("Attachment not found");
        return;
      }
      
      const { error: storageError } = await supabase.storage
        .from('journal_attachments')
        .remove([attachment.file_path]);
      
      if (storageError) {
        toast.error(`Error removing file: ${storageError.message}`);
        return;
      }

      const { error: dbError } = await supabase
        .from('journal_attachments')
        .delete()
        .eq('id', attachmentId);
      
      if (dbError) {
        toast.error(`Error deleting attachment record: ${dbError.message}`);
        return;
      }

      toast.success('Attachment deleted successfully');
      
      // Invalidate the attachments query to trigger a refetch
      await queryClient.invalidateQueries({ queryKey: ["journal-attachments"] });
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast.error('Failed to delete attachment');
    }
  };

  if (isLoadingEntries || isLoadingAttachments) return <div className="p-8 text-center">Loading...</div>;
  if (!entries) return <div className="p-8 text-center">No entries found.</div>;

  // Show all entries
  const filteredEntries = entries.filter(
    (entry) =>
      entry.mood.toLowerCase().includes(search.toLowerCase()) ||
      entry.content.toLowerCase().includes(search.toLowerCase()) ||
      (entry.emotion_type && entry.emotion_type.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="relative min-h-screen w-full">
      {/* Full-screen blue gradient background */}
      <div className="fixed inset-0 w-full h-full bg-gradient-to-b from-blue-50 to-white z-0" />
      <div className="relative z-10 min-h-screen w-full">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-800">All Journal Entries</h1>
            <Button variant="outline" onClick={() => navigate("/journal")}>Back to Journal</Button>
          </div>
          <div className="mb-6">
            <Input
              placeholder="Search entries by mood, content, or emotion..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="max-w-md"
            />
          </div>
          <JournalEntryList
            entries={filteredEntries}
            entryAttachments={entryAttachments || {}}
            onAttachmentDelete={handleRemoveEntryAttachment}
            showAll={true}
            deletable={true}
            onDeleteSelected={handleDeleteSelectedEntries}
          />
        </div>
      </div>
    </div>
  );
};

export default AllJournalEntries; 